import { z } from 'zod';
import { ChainMarketRegionSchema } from '../economy-chain/domain.js';

/**
 * The Gold result manifest an `economy.product-research.v1` run produces (ECONOMY-10 C3 / ADR 0099 §10).
 *
 * This is the contract between the agent and the projection, and it is deliberately strict: a run is
 * only `ready` once its manifest **parsed**, so a malformed or half-written result fails visibly
 * rather than landing partial rows on the board.
 *
 * Every product carries its **natural key** (normalised domain + slug, ADR 0104 §5), minted by the
 * Silver transform rather than the model — that is what lets a replayed completion event update the
 * same rows instead of creating a second set.
 */

export const ECONOMY_RESEARCH_RESULT_VERSION = 'economy.product-research-result.v1' as const;

/**
 * One capability claim, and the exact words that support it.
 *
 * **`quote` is stored inline, not only `blockId`** (ADR 0110 / the plan's I0). A pointer alone
 * means the citation renders only when the warehouse is reachable, and a citation that can fail
 * to load is worse than none: the analyst cannot tell "unsourced" from "temporarily unavailable".
 * `blockId` rides along for provenance and re-verification.
 */
export const ResearchResultFeatureSchema = z.object({
  /** A short, concrete capability claim — "Hold and spend in 30 currencies". */
  feature: z.string().min(1).max(300),
  /** The consumer need it answers, when the extractor could state one. */
  need: z.string().max(300).default(''),
  /** The page the claim was taken from. Required: an uncited feature is dropped, not carried. */
  sourceUrl: z.string().min(1).max(500),
  sourceName: z.string().max(200).default(''),
  /** The block of text that supports the claim, verbatim. */
  quote: z.string().min(1).max(1000),
  /** The addressable block in `silver.source_blocks` the quote came from. */
  blockId: z.string().max(200).default(''),
});
export type ResearchResultFeature = z.infer<typeof ResearchResultFeatureSchema>;

/**
 * One real public comment about the product.
 *
 * `kind` is the honesty valve. A Reddit thread found by searching a product's NAME may be about
 * something else entirely — a product called "Halo" matches a great deal that is not the product
 * — so a match is a **mention** unless the product's own domain appears in the thread, in which
 * case it is a **review**. Overclaiming a match is the same defect as an uncited feature.
 */
export const ResearchResultOpinionSchema = z.object({
  source: z.enum(['producthunt', 'reddit']),
  kind: z.enum(['review', 'mention']),
  /** Stable id at the source, so a redelivery updates the row rather than adding one. */
  externalId: z.string().min(1).max(200),
  /** The comment, verbatim. Never summarised. */
  body: z.string().min(1).max(4000),
  authorName: z.string().max(200).default(''),
  authorUrl: z.string().max(500).default(''),
  permalink: z.string().min(1).max(500),
  /** ISO 8601, as the source reported it. */
  postedAt: z.string().max(64).default(''),
  score: z.number().int().nullable().default(null),
  /** The query that found it — the thing that makes a `mention` auditable. */
  matchedBy: z.string().max(300).default(''),
});
export type ResearchResultOpinion = z.infer<typeof ResearchResultOpinionSchema>;

/**
 * Fields the projection is allowed to write. Everything a *person* sets — verdict, notes, category,
 * dispute, status — is the operational overlay (ADR 0104 §6) and is deliberately absent from this
 * schema, so a re-projection has no way to overwrite an analyst's judgement even by accident.
 */
export const ResearchResultProductSchema = z.object({
  /** `domain/slug`, e.g. `cursor.com/cursor`. The upsert key. */
  productKey: z.string().min(3).max(300),
  name: z.string().min(1).max(200),
  company: z.string().max(200).optional(),
  url: z.string().max(500).optional(),
  /** One-line description in the agent's own words. */
  summary: z.string().max(1000).optional(),
  /**
   * The GICS industry group this product sits in — the board's **subcategory** (ECONOMY-21 I1).
   *
   * Absent means the research could not place it, which the board renders as "Unfiled". It is
   * deliberately not defaulted: filing every unplaced product under one group would put a
   * confident answer where there is none, which is the defect the taxonomy reset removed.
   */
  subcategory: z.string().max(120).optional(),
  /**
   * Where the product is actually available, from what the source says (ECONOMY-21 I2). An empty
   * list means the research could not establish it — a different claim from "Global".
   */
  regions: z.array(ChainMarketRegionSchema).max(10).default([]),
  /**
   * Capability claims extracted from the product's own pages, each **cited to the block of text
   * it came from** and each already through the fact-check (ECONOMY-21 I4).
   *
   * A feature with no citation never reaches this list: the extractor drops it and reports the
   * drop in `featuresDropped`, so "we found 12 and kept 9" is visible rather than silent.
   */
  features: z.array(ResearchResultFeatureSchema).max(60).default([]),
  /**
   * How many extracted features the fact-check refused. Carried so an empty feature list can be
   * told apart from one that was emptied — the two mean very different things about a product.
   */
  featuresDropped: z.number().int().nonnegative().default(0),
  /**
   * Verbatim public comments about the product, with a real author and a real permalink
   * (ECONOMY-21 I6). Never summarised: a paraphrased review is an unsourced claim.
   */
  opinions: z.array(ResearchResultOpinionSchema).max(200).default([]),
  /** Where each claim came from. An empty list is legal and means "asserted without a citation". */
  evidence: z.array(z.object({
    claim: z.string().min(1).max(500),
    sourceUrl: z.string().max(500),
  })).max(50).default([]),
  /**
   * Whether the warehouse already held this product before the run that produced this manifest
   * (ECONOMY-19 I2). Only the agent can answer it: it asks Silver *before* its own upsert, and by
   * the time the projection reads this every product looks equally present.
   *
   * This is what lets a re-run read as "here is what changed" — the round lists the new finds and
   * tucks the re-found ones behind a disclosure, instead of repeating itself in full.
   *
   * Defaults to `false` so a manifest written before this field existed still parses and projects,
   * as a round of genuinely new products rather than one hidden entirely behind a disclosure.
   */
  seenBefore: z.boolean().default(false),
});
export type ResearchResultProduct = z.infer<typeof ResearchResultProductSchema>;

export const ResearchResultManifestSchema = z.object({
  version: z.literal(ECONOMY_RESEARCH_RESULT_VERSION),
  researchRunId: z.string().min(1),
  agentRunId: z.string().min(1),
  subject: z.object({ type: z.string().min(1), id: z.string().min(1) }),
  producedAt: z.number().int().nonnegative(),
  products: z.array(ResearchResultProductSchema).max(500),
  /**
   * Candidates the agent found but could not key (ADR 0104 §5 refuses to guess identity). Carried so
   * the count is visible rather than the rows silently vanishing between the agent and the board.
   */
  unkeyed: z.array(z.object({
    name: z.string().max(200),
    url: z.string().max(500).optional(),
    reason: z.string().max(300),
  })).max(500).default([]),
  /**
   * A caveat about the result *as a whole*, in the agent's own words — "nothing in the landed
   * sources matches objective B, because none of them carry a geography field".
   *
   * A finding, not a failure, and the reason this field exists at all: a run whose scope the data
   * cannot serve returns zero products, and zero products under a bare "Ready" badge reads as "this
   * market is empty" rather than "we cannot see this market yet". Carried to the board so the
   * difference is visible.
   */
  note: z.string().max(600).optional(),
});
export type ResearchResultManifest = z.infer<typeof ResearchResultManifestSchema>;
